import { AEMComponentGrid } from '@johnlewispartnership/wtr-aem-components';
import { type ContentChildComponent } from '@johnlewispartnership/wtr-aem-components/dist/common/types';
import PageHeadMeta from '@/components/HeadMeta/PageHeadMeta';
import { getDocumentTitle } from '@/common/utils/document-title';
import LinkComponent from '@/components/LinkComponent/LinkComponent';
import Script from 'next/script';
import configuration from '@/configuration';
import styles from './home.module.scss';
import { type HomePageProps } from './types';
import { ProductRecommendations } from './ProductRecommendations/ProductRecommendations';
import dataLayer from '@/services/analytics/data-layer/server-safe-data-layer';

const { AEM_CF_DOMAIN: cfDomain } = configuration;

const getContainerContent = (container?: ContentChildComponent): ContentChildComponent[] => {
  if (
    container?.childComponents &&
    container.childComponents.length > 0 &&
    container.childComponents[0].childComponents
  ) {
    return container.childComponents[0].childComponents;
  }
  return [];
};

function HomePage(props: HomePageProps) {
  const { contentNodes, nonce, recommendedProducts } = props;

  const homePageTopContent = getContainerContent(contentNodes[0]);
  const homePageBottomContent = getContainerContent(contentNodes[1]);
  return (
    <div className={styles.homepage} data-testid="home-page-content">
      <h1 className="srOnly">Waitrose Cellar Home</h1>
      <Script
        id="Organization-schema"
        nonce={nonce}
        strategy="afterInteractive"
        type="application/ld+json"
      >
        {`{
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Cellar by Waitrose & Partners",
            "url": "https://www.waitrosecellar.com/",
            "address": {
              "@type": "PostalAddress",
              "addressRegion": "South East",
              "addressLocality": "Bracknell",
              "addressCountry": "GB",
              "postalCode": "RG12 8YA",
              "streetAddress": "Doncastle Road"
            },
            "logo": "https://www.waitrosecellar.com/images/logos/cellar_logo_secondary_stacked.svg",
            "description": "Visit Waitrose Cellar to browse & buy from our expertly chosen selection of quality red & white wine, champagne, prosecco & more.",
            "sameAs": [],
            "contactPoint": {
              "@type": "contactPoint",
              "email": "waitrosecellar@waitrose.co.uk",
              "telephone": "0800 188 881"
            }
          }`}
      </Script>
      <PageHeadMeta
        canonicalUrl="https://www.waitrosecellar.com/"
        metaTitle={getDocumentTitle('home')}
      />
      <AEMComponentGrid
        asContentLocation
        childComponents={homePageTopContent}
        dataLayer={dataLayer}
        environmentalConfig={{
          cfDomain,
        }}
        linkComponent={LinkComponent}
        name="home-top-grid"
      />
      {recommendedProducts.length > 0 && (
        <ProductRecommendations recommendedProducts={recommendedProducts} />
      )}
      {homePageBottomContent.length > 0 && (
        <AEMComponentGrid
          asContentLocation
          childComponents={homePageBottomContent}
          dataLayer={dataLayer}
          environmentalConfig={{
            cfDomain,
          }}
          linkComponent={LinkComponent}
          name="home-bottom-grid"
        />
      )}
    </div>
  );
}

HomePage.layoutProps = { variant: 'maximum-width' };

export default HomePage;
